import {
  Pressable,
  View,
  type GestureResponderEvent,
  type PressableProps,
} from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
} from "react-native-reanimated";
import { haptic as triggerHaptic, type HapticStyle } from "../haptics";
import { playSound, type SoundStyle } from "../sounds";
import { springs } from "../theme/tokens";
import { getInteractionDelta } from "../utils/interactionStyles";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type Props = PressableProps & {
  activeOpacity?: number;
  activeScale?: number;
  haptic?: HapticStyle;
  sound?: SoundStyle;
  /** Show hover + focus visual feedback (web). Defaults to true. */
  feedback?: boolean;
  /** Stable analytics identifier, required when the label is translated. */
  action?: string;
};

export function MoonPressable({
  activeOpacity = 0.7,
  activeScale = 0.97,
  haptic: hapticStyle,
  sound: soundStyle,
  feedback = true,
  action,
  style,
  children,
  onPress,
  onPressIn,
  onPressOut,
  disabled,
  ...rest
}: Props) {
  const scale = useSharedValue(1);
  const opacity = useSharedValue(1);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handlePressIn = (e: GestureResponderEvent) => {
    scale.value = withSpring(activeScale, springs.press);
    opacity.value = withSpring(activeOpacity, springs.press);
    onPressIn?.(e);
  };

  const handlePressOut = (e: GestureResponderEvent) => {
    scale.value = withSpring(1, springs.press);
    opacity.value = withSpring(1, springs.press);
    onPressOut?.(e);
  };

  const capturePress = usePressCapture("MoonPressable");
  const handlePress = (e: GestureResponderEvent) => {
    capturePress(pressLabel(rest.accessibilityLabel, rest.testID), action);
    if (hapticStyle) triggerHaptic(hapticStyle);
    if (soundStyle) playSound(soundStyle);
    onPress?.(e);
  };

  const staticStyle = typeof style === "function" ? undefined : style;

  return (
    <Animated.View style={animatedStyle}>
      <Pressable
        onPress={handlePress}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        disabled={disabled}
        {...rest}
      >
        {({ pressed, hovered, focused }) => (
          <View
            style={[
              typeof style === "function"
                ? style({ pressed, hovered, focused })
                : staticStyle,
              feedback
                ? getInteractionDelta(
                    { hovered, focused },
                    { disabled: !!disabled },
                  )
                : undefined,
            ]}
          >
            {typeof children === "function"
              ? children({ pressed, hovered, focused })
              : children}
          </View>
        )}
      </Pressable>
    </Animated.View>
  );
}
